import { Link } from '@tanstack/react-router'
import { FC } from 'react'
import { useImportRecordingsPicker } from '~/features/importer'
import {
  useCurrentOrganization,
  useCurrentOrganizationHistory,
  useCurrentUser,
  useUserHomepageView,
} from '~/hooks'

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
  Icon,
} from '@hap/components'
import {
  HOMEPAGE_VIEW_ANALYTICS,
  HOMEPAGE_VIEW_SCORECARD,
  HomepageViewLabels,
} from '@hap/constants'

import { OrganizationAvatar } from './organization-avatar'

export const CurrentOrganizationAvatar: FC = () => {
  const organization = useCurrentOrganization()
  const currentUser = useCurrentUser()
  const [homepageView, setHomepageView] = useUserHomepageView()
  const availableOrganizations = useCurrentOrganizationHistory()
  const importRecordings = useImportRecordingsPicker()
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="hover:bg-accent data-[state=open]:bg-accent -m-2 flex items-center gap-2 rounded-md p-2 text-left">
          <OrganizationAvatar
            name={organization.name}
            branding={organization.branding}
            iconFileId={organization.iconFileId}
            rounded="sm"
          />
          <span className="shrink grow truncate font-semibold">{organization.name}</span>
          <Icon
            name="triangle"
            weight="fill"
            size="8"
            className="shrink-0 rotate-180 opacity-50"
          />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-70">
        <DropdownMenuSub>
          <DropdownMenuSubTrigger icon="cube">Switch organization</DropdownMenuSubTrigger>
          <DropdownMenuSubContent>
            <DropdownMenuGroup>
              {Object.keys(availableOrganizations).map((orgId) => {
                const org = availableOrganizations[orgId]
                return (
                  <DropdownMenuItem key={orgId} asChild>
                    <a href={`/org/${orgId}/`} className="flex items-center">
                      <OrganizationAvatar
                        size="sm"
                        rounded="sm"
                        name={org.name}
                        branding={org.branding}
                        className="shrink-0"
                      />
                      <span className="shrink grow truncate">{org.name}</span>
                      {!org.activeSession && <i className="shrink-0">Logged Out</i>}
                    </a>
                  </DropdownMenuItem>
                )
              })}
              <DropdownMenuItem asChild>
                <Link to="/" search={{ force: true }}>
                  Add another organization
                </Link>
              </DropdownMenuItem>
            </DropdownMenuGroup>
          </DropdownMenuSubContent>
        </DropdownMenuSub>

        <DropdownMenuSeparator />
        <DropdownMenuGroup>
          <DropdownMenuItem icon="upload-simple" onSelect={importRecordings}>
            Import recordings
          </DropdownMenuItem>
        </DropdownMenuGroup>

        <DropdownMenuSeparator />
        <DropdownMenuGroup>
          <DropdownMenuItem asChild>
            <Link to="/$alias/settings/profile" from="/$alias">
              <Icon name="user-circle" />
              {currentUser?.firstName} {currentUser?.lastName}
            </Link>
          </DropdownMenuItem>
          <DropdownMenuLabel>Default Homepage</DropdownMenuLabel>
          <DropdownMenuItem
            icon="chart-bar"
            onSelect={() => setHomepageView(HOMEPAGE_VIEW_ANALYTICS)}
          >
            {HomepageViewLabels[HOMEPAGE_VIEW_ANALYTICS]}
            {homepageView === HOMEPAGE_VIEW_ANALYTICS && (
              <Icon name="check" className="ml-auto" />
            )}
          </DropdownMenuItem>
          <DropdownMenuItem
            onSelect={() => setHomepageView(HOMEPAGE_VIEW_SCORECARD)}
            className="gap-2"
            icon="user-circle"
          >
            {HomepageViewLabels[HOMEPAGE_VIEW_SCORECARD]}
            {homepageView === HOMEPAGE_VIEW_SCORECARD && (
              <Icon name="check" className="ml-auto" />
            )}
          </DropdownMenuItem>
        </DropdownMenuGroup>
        <DropdownMenuSeparator />
        <DropdownMenuGroup>
          <DropdownMenuItem asChild>
            <Link to="/logout">
              <Icon name="sign-out" />
              Sign out
            </Link>
          </DropdownMenuItem>
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
